/**
 * Cached read API – schedule, dashboard, tournament list, feed.
 * 1. Return from cache immediately for instant load, then refresh as delta (full fetch with asOf).
 * 2. Backend returns asOf; we store it and pass optional since on next fetch (future: backend delta).
 * 3. Conflicts: when server data has updatedAt > cache for an entity we changed locally, surface for admin.
 */

import { getFunctions, httpsCallable } from 'firebase/functions';
import app from '../firebase/config';
import { getActAsUserId } from '../impersonationStore';
import {
  getCachedData,
  setCached,
  getCacheAsOf,
  patchCached,
  isCacheStale,
  isCacheOlderThan,
  getCacheTtlMs,
  CACHE_KEYS,
  FULL_REFRESH_TTL_MS_EXPORT as FULL_REFRESH_TTL_MS,
} from '../services/dataCache';

const functions = getFunctions(app, 'us-central1');

function call(name, data = {}) {
  const fn = httpsCallable(functions, name);
  const actAs = getActAsUserId();
  const payload = actAs ? { ...data, actAsUserId: actAs } : data;
  return fn(payload).then((res) => res.data);
}

const inFlight = new Map();

function cacheKey(base, actAs) {
  return actAs ? `${base}_as_${actAs}` : base;
}

function dedupe(key, fn) {
  let p = inFlight.get(key);
  if (p) return p;
  p = fn().finally(() => { inFlight.delete(key); });
  inFlight.set(key, p);
  return p;
}

/**
 * Get landing page data (public, no auth). Cache-first, 5 min TTL. No polling.
 */
export async function getLandingData(options = {}) {
  const key = CACHE_KEYS.landing();
  const skipCache = options.skipCache === true;

  if (!skipCache) {
    const cached = getCachedData(key);
    if (cached?.gamesData != null && !isCacheStale(key)) {
      const fetchPromise = dedupe('getLandingData', () => call('getLandingData', {}));
      fetchPromise.then((fresh) => {
        if (fresh?.gamesData != null) setCached(key, fresh, fresh.asOf ?? Date.now());
      }).catch(() => {});
      return { ...cached, fromCache: true };
    }
  }

  const fetchKey = 'getLandingData';
  const fresh = await dedupe(fetchKey, () => call('getLandingData', {}));
  if (fresh?.gamesData != null) setCached(key, fresh, fresh.asOf ?? Date.now());
  return fresh ?? { gameIds: [], gamesData: {}, asOf: Date.now() };
}

/** Merge delta schedule: apply matchIds (drop removed), then overwrite/append by id; sort by scheduledAt. */
function mergeSchedule(cached, fresh) {
  if (!cached?.matches || !fresh?.delta || !Array.isArray(fresh.matches)) return cached;
  const validIds = Array.isArray(fresh.matchIds) ? new Set(fresh.matchIds) : null;
  const byId = {};
  cached.matches.forEach((m) => {
    if (!m?.id) return;
    if (validIds != null && !validIds.has(m.id)) return;
    byId[m.id] = m;
  });
  fresh.matches.forEach((m) => { if (m?.id) byId[m.id] = m; });
  const matches = Object.values(byId).sort((a, b) => (a.scheduledAt ?? 0) - (b.scheduledAt ?? 0));
  return { ...cached, matches, asOf: fresh.asOf, playerNames: { ...cached.playerNames, ...fresh.playerNames } };
}

/** Merge delta dashboard: apply matchIds (drop removed), then merge arrays by id. */
function mergeDashboard(cached, fresh) {
  if (!fresh?.delta) return cached;
  const validIds = Array.isArray(fresh.matchIds) ? new Set(fresh.matchIds) : null;
  const mergeById = (existing, incoming, sortBy) => {
    const byId = {};
    (existing || []).forEach((m) => {
      if (!m?.id) return;
      if (validIds != null && !validIds.has(m.id)) return;
      byId[m.id] = m;
    });
    (incoming || []).forEach((m) => { if (m?.id) byId[m.id] = m; });
    const list = Object.values(byId);
    if (sortBy) list.sort(sortBy);
    return list;
  };
  const byScheduled = (a, b) => (a.scheduledAt ?? a.scheduledDate ?? 0) - (b.scheduledAt ?? b.scheduledDate ?? 0);
  const byCompletedDesc = (a, b) => (b.completedAt ?? 0) - (a.completedAt ?? 0);
  return {
    ...cached,
    tournamentMatches: mergeById(cached?.tournamentMatches, fresh.tournamentMatches, byScheduled),
    liveMatches: mergeById(cached?.liveMatches, fresh.liveMatches),
    recentCompletedMatches: mergeById(cached?.recentCompletedMatches, fresh.recentCompletedMatches, byCompletedDesc),
    asOf: fresh.asOf,
  };
}

/** Merge delta feed: items by stable key (match-id or log-id). */
function mergeFeed(cached, fresh) {
  if (!cached?.items || !fresh?.delta || !Array.isArray(fresh.items)) return cached;
  const byKey = {};
  cached.items.forEach((item) => {
    const k = item.kind === 'match_completed' ? `match-${item.matchId}` : `log-${item.id}`;
    byKey[k] = item;
  });
  fresh.items.forEach((item) => {
    const k = item.kind === 'match_completed' ? `match-${item.matchId}` : `log-${item.id}`;
    byKey[k] = item;
  });
  const items = Object.values(byKey);
  const completedAt = (i) => i.completedAt ?? 0;
  const performedAt = (i) => i.performedAt ?? 0;
  items.sort((a, b) => Math.max(completedAt(b), performedAt(b)) - Math.max(completedAt(a), performedAt(a)));
  return { ...cached, items, asOf: fresh.asOf, matchDetails: { ...cached.matchDetails, ...fresh.matchDetails }, playerNames: { ...cached.playerNames, ...fresh.playerNames } };
}

/**
 * Get schedule data: cache-first, then fetch. Response includes asOf.
 */
export async function getScheduleData(gameId = 'table-tennis', options = {}) {
  const actAs = getActAsUserId();
  const key = cacheKey(CACHE_KEYS.schedule(gameId), actAs);
  const forceFullRefresh = options.skipCache === true || isCacheOlderThan(key, FULL_REFRESH_TTL_MS);

  if (!forceFullRefresh) {
    const cached = getCachedData(key);
    if (Array.isArray(cached?.matches) && cached?.asOf != null) {
      const since = getCacheAsOf(key);
      const fetchKey = `getScheduleData_${gameId}${actAs ? `_${actAs}` : ''}`;
      const fetchPromise = dedupe(fetchKey, () =>
        call('getScheduleData', { gameId, since: since || undefined })
      );
      fetchPromise
        .then((fresh) => {
          if (fresh?.unchanged && fresh?.asOf != null) {
            const prev = getCachedData(key);
            if (prev) setCached(key, prev, fresh.asOf);
            return;
          }
          if (fresh?.asOf == null) return;
          const prev = getCachedData(key);
          const toStore = fresh.delta && prev ? mergeSchedule(prev, fresh) : fresh;
          setCached(key, toStore, fresh.asOf);
        })
        .catch(() => {});
      return { ...cached, fromCache: true };
    }
  }

  const since = forceFullRefresh ? undefined : getCacheAsOf(key);
  const fetchKey = `getScheduleData_${gameId}${actAs ? `_${actAs}` : ''}`;
  const fresh = await dedupe(fetchKey, () =>
    call('getScheduleData', { gameId, since: since || undefined })
  );
  if (fresh?.unchanged && fresh?.asOf != null) {
    const cached = getCachedData(key);
    if (cached) setCached(key, cached, fresh.asOf);
    return getCachedData(key) ?? cached ?? fresh;
  }
  if (fresh?.asOf != null) {
    const prev = getCachedData(key);
    const toStore = fresh.delta && prev ? mergeSchedule(prev, fresh) : fresh;
    setCached(key, toStore, fresh.asOf);
  }
  return fresh.delta && getCachedData(key) ? getCachedData(key) : fresh;
}

/**
 * Get dashboard data: cache-first, then fetch.
 */
export async function getDashboardData(gameId = 'table-tennis', options = {}) {
  const actAs = getActAsUserId();
  const key = cacheKey(CACHE_KEYS.dashboard(gameId), actAs);
  const forceFullRefresh = options.skipCache === true || isCacheOlderThan(key, FULL_REFRESH_TTL_MS);

  if (!forceFullRefresh) {
    const cached = getCachedData(key);
    if (cached?.activeTournament !== undefined || cached?.tournamentMatches) {
      const since = getCacheAsOf(key);
      const fetchKey = `getDashboardData_${gameId}${actAs ? `_${actAs}` : ''}`;
      const fetchPromise = dedupe(fetchKey, () =>
        call('getDashboardData', { gameId, since: since || undefined })
      );
      fetchPromise
        .then((fresh) => {
          if (fresh?.unchanged && fresh?.asOf != null) {
            const prev = getCachedData(key);
            if (prev) setCached(key, prev, fresh.asOf);
            return;
          }
          if (fresh?.asOf == null) return;
          const prev = getCachedData(key);
          const toStore = fresh.delta && prev ? mergeDashboard(prev, fresh) : fresh;
          setCached(key, toStore, fresh.asOf);
        })
        .catch(() => {});
      return { ...cached, fromCache: true };
    }
  }

  const since = forceFullRefresh ? undefined : getCacheAsOf(key);
  const fetchKey = `getDashboardData_${gameId}${actAs ? `_${actAs}` : ''}`;
  const fresh = await dedupe(fetchKey, () =>
    call('getDashboardData', { gameId, since: since || undefined })
  );
  if (fresh?.unchanged && fresh?.asOf != null) {
    const cached = getCachedData(key);
    if (cached) setCached(key, cached, fresh.asOf);
    return getCachedData(key) ?? cached ?? fresh;
  }
  if (fresh?.asOf != null) {
    const prev = getCachedData(key);
    const toStore = fresh.delta && prev ? mergeDashboard(prev, fresh) : fresh;
    setCached(key, toStore, fresh.asOf);
  }
  return fresh.delta && getCachedData(key) ? getCachedData(key) : fresh;
}

/**
 * Get tournament list: cache-first, then fetch.
 */
export async function getTournamentList(gameId = 'table-tennis', options = {}) {
  const actAs = getActAsUserId();
  const key = cacheKey(CACHE_KEYS.tournamentList(gameId), actAs);
  const skipCache = options.skipCache === true;

  if (!skipCache) {
    const cached = getCachedData(key);
    if (cached?.tournaments) {
      const since = getCacheAsOf(key);
      const fetchKey = `getTournamentList_${gameId}${actAs ? `_${actAs}` : ''}`;
      const fetchPromise = dedupe(fetchKey, () =>
        call('getTournamentList', { gameId, since: since || undefined })
      );
      fetchPromise.then((fresh) => {
        if (fresh?.asOf != null) setCached(key, fresh, fresh.asOf);
      }).catch(() => {});
      return { ...cached, fromCache: true };
    }
  }

  const fetchKey = `getTournamentList_${gameId}${actAs ? `_${actAs}` : ''}`;
  const fresh = await dedupe(fetchKey, () =>
    call('getTournamentList', { gameId })
  );
  if (fresh?.asOf != null) setCached(key, fresh, fresh.asOf);
  return fresh;
}

/**
 * Get feed updates: cache-first, then fetch.
 */
export async function getFeedUpdates(gameId = 'table-tennis', options = {}) {
  const actAs = getActAsUserId();
  const key = cacheKey(CACHE_KEYS.feed(gameId), actAs);
  const skipCache = options.skipCache === true;

  if (!skipCache) {
    const cached = getCachedData(key);
    if (cached?.items) {
      const since = getCacheAsOf(key);
      const fetchKey = `getFeedUpdates_${gameId}${actAs ? `_${actAs}` : ''}`;
      const fetchPromise = dedupe(fetchKey, () =>
        call('getFeedUpdates', { gameId, since: since || undefined })
      );
      fetchPromise.then((fresh) => {
        if (fresh?.unchanged && fresh?.asOf != null) {
          const prev = getCachedData(key);
          if (prev) setCached(key, prev, fresh.asOf);
          return;
        }
        if (fresh?.asOf == null) return;
        const prev = getCachedData(key);
        const toStore = fresh.delta && prev ? mergeFeed(prev, fresh) : fresh;
        setCached(key, toStore, fresh.asOf);
      }).catch(() => {});
      return { ...cached, fromCache: true };
    }
  }

  const since = skipCache ? undefined : getCacheAsOf(key);
  const fetchKey = `getFeedUpdates_${gameId}${actAs ? `_${actAs}` : ''}`;
  const fresh = await dedupe(fetchKey, () =>
    call('getFeedUpdates', { gameId, since: since || undefined })
  );
  if (fresh?.unchanged && fresh?.asOf != null) {
    const cached = getCachedData(key);
    if (cached) setCached(key, cached, fresh.asOf);
    return getCachedData(key) ?? cached ?? fresh;
  }
  if (fresh?.asOf != null) {
    const prev = getCachedData(key);
    const toStore = fresh.delta && prev ? mergeFeed(prev, fresh) : fresh;
    setCached(key, toStore, fresh.asOf);
  }
  return fresh.delta && getCachedData(key) ? getCachedData(key) : fresh;
}

/**
 * Get statistics rankings only. Completed-match data: cached 30 days; no request if cache valid.
 */
export async function getStatisticsRankings(gameId = 'table-tennis', options = {}) {
  const actAs = getActAsUserId();
  const key = cacheKey(CACHE_KEYS.statistics(gameId, 'rankings'), actAs);
  const skipCache = options.skipCache === true;

  if (!skipCache) {
    const cached = getCachedData(key);
    if ((cached?.rankingsSingles != null || cached?.rankingsDoubles != null) && !isCacheStale(key, getCacheTtlMs(key))) {
      return { ...cached, fromCache: true };
    }
  }

  const since = getCacheAsOf(key);
  const fetchKey = `getStatisticsData_rankings_${gameId}${actAs ? `_${actAs}` : ''}`;
  const fresh = await dedupe(fetchKey, () =>
    call('getStatisticsData', { gameId, part: 'rankings', since: since || undefined })
  );
  if (fresh?.unchanged) {
    const cached = getCachedData(key);
    if (cached != null && fresh?.asOf != null) setCached(key, cached, fresh.asOf);
    return getCachedData(key) ?? fresh;
  }
  if (fresh?.asOf != null) setCached(key, fresh, fresh.asOf);
  return fresh;
}

/**
 * Get statistics recent matches only. Completed-match data: cached 30 days; no request if cache valid.
 */
export async function getStatisticsRecentMatches(gameId = 'table-tennis', options = {}) {
  const actAs = getActAsUserId();
  const key = cacheKey(CACHE_KEYS.statistics(gameId, 'recent'), actAs);
  const skipCache = options.skipCache === true;

  if (!skipCache) {
    const cached = getCachedData(key);
    if (cached?.recentMatches && !isCacheStale(key, getCacheTtlMs(key))) {
      return { ...cached, fromCache: true };
    }
  }

  const since = getCacheAsOf(key);
  const fetchKey = `getStatisticsData_recent_${gameId}${actAs ? `_${actAs}` : ''}`;
  const fresh = await dedupe(fetchKey, () =>
    call('getStatisticsData', { gameId, part: 'recent', since: since || undefined })
  );
  if (fresh?.unchanged) {
    const cached = getCachedData(key);
    if (cached != null && fresh?.asOf != null) setCached(key, cached, fresh.asOf);
    return getCachedData(key) ?? fresh;
  }
  if (fresh?.asOf != null) setCached(key, fresh, fresh.asOf);
  return fresh;
}

/**
 * Player profile + completed match data. Cached 30 days in localStorage; no refetch until expiry.
 */
export async function getPlayerProfileData(targetUserId, options = {}) {
  const cacheKey = CACHE_KEYS.playerProfile(targetUserId);
  const skipCache = options.skipCache === true;

  if (!skipCache) {
    const cached = getCachedData(cacheKey);
    if (cached != null && (cached.profile != null || cached.matchSummaries) && !isCacheStale(cacheKey, getCacheTtlMs(cacheKey))) {
      return { ...cached, fromCache: true };
    }
  }

  const since = getCacheAsOf(cacheKey);
  const fresh = await dedupe(`getPlayerProfileData_${targetUserId}`, () =>
    call('getPlayerProfileData', { targetUserId, since: since || undefined })
  );
  if (fresh?.unchanged) {
    const cached = getCachedData(cacheKey);
    if (cached != null && fresh?.asOf != null) setCached(cacheKey, cached, fresh.asOf);
    return getCachedData(cacheKey) ?? cached ?? fresh;
  }
  if (fresh != null) setCached(cacheKey, fresh, fresh.asOf ?? Date.now());
  return fresh;
}

/**
 * Patch a single match in the schedule cache. Call after backend mutation success
 * so cache stays up to date without a full refetch.
 */
export function patchScheduleMatch(gameId, matchId, updates) {
  const key = CACHE_KEYS.schedule(gameId);
  patchCached(key, (data) => {
    const matches = data?.matches || [];
    const idx = matches.findIndex((m) => m?.id === matchId);
    if (idx < 0) return data;
    const next = [...matches];
    next[idx] = { ...next[idx], ...updates };
    return { ...data, matches: next };
  });
}

/**
 * Patch a single match in the dashboard cache (tournamentMatches, liveMatches, recentCompletedMatches).
 */
export function patchDashboardMatch(gameId, matchId, updates) {
  const key = CACHE_KEYS.dashboard(gameId);
  patchCached(key, (data) => {
    const patchArr = (arr) => {
      if (!Array.isArray(arr)) return arr;
      const idx = arr.findIndex((m) => m?.id === matchId);
      if (idx < 0) return arr;
      const next = [...arr];
      next[idx] = { ...next[idx], ...updates };
      return next;
    };
    return {
      ...data,
      tournamentMatches: patchArr(data?.tournamentMatches),
      liveMatches: patchArr(data?.liveMatches),
      recentCompletedMatches: patchArr(data?.recentCompletedMatches),
    };
  });
}

export { CACHE_KEYS, invalidateCache } from '../services/dataCache';
